import { AbstractControl, FormGroup } from '@angular/forms';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';

export interface PermissionOptions {
  name: string;
  children?: Array<PermissionOptions>;
}

export function CheckPermission(parentPermission: string, childPermission: string, authService: AuthenticationService) {
  let authData = authService.authData;
  if (!authData && !authData.Features) {
    return false;
  }
  const features = authData.Features;
  const parent = features.find((x: any) => x.Name === parentPermission);
  const child = features.find((x: any) => x.Name === childPermission);
  if (childPermission) {
    if (child == undefined || parent == undefined) return false;

    if (child == undefined) return false;

    if (child.name != parent.name) return false;
  }
  if (parent == undefined) return false;

  return true;
}

export const momentCustom = (isUTC = false, timezone = 'America/New_York') => {
  // Setting default timezone
  if (timezone) moment.tz.setDefault(timezone);

  return { ...moment, moment: moment };
};

export const momentCustomInstance = (...arg: any) => momentCustom().moment(...arg);

export const getESTHourFromUTC = (hour: any) => {
  var NewYork_tz_offset = momentCustom().tz('America/New_York').utcOffset();
  let estTime = moment([moment().year(), moment().month(), moment().format('D'), hour, 0, 0]).add(
    NewYork_tz_offset,
    'minute'
  );
  return estTime.format('HH');
};

export const compareValues = (key: any, order = 'asc') => {
  return function innerSort(a: any, b: any) {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      // property doesn't exist on either object
      return 0;
    }

    const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
    const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return order === 'desc' ? comparison * -1 : comparison;
  };
};

//20s,1s
export const compareValuesString = (key: any, order = 'asc') => {
  return function innerSort(a: any, b: any) {
    if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
      // property doesn't exist on either object
      return 0;
    }

    return a[key].localeCompare(b[key], undefined, { numeric: true, sensitivity: 'base' });
  };
};

/**
 * sortList function to sort array.
 * @param list                Array to be sort
 * @param isAdvanceSort       Enable advance sort
 * @param advanceSortOptions  Advance sort options
 */
export const sortList = (
  list: Array<any>,
  isAdvanceSort = false,
  advanceSortOptions: { key: string; asc: boolean } | undefined = undefined
) => {
  const { compare } = Intl.Collator('en-US');
  let sortCompareFunction = compare;
  if (isAdvanceSort && advanceSortOptions) {
    sortCompareFunction = advanceSortOptions.asc
      ? (a: any, b: any) => compare(a[advanceSortOptions.key], b[advanceSortOptions.key])
      : (a: any, b: any) => compare(b[advanceSortOptions.key], a[advanceSortOptions.key]);
  }
  list.sort(sortCompareFunction);
};

/**
 * Copy to clipboard
 * @param textToCopy    text you want to copy
 */
export const copyToClipboard = (textToCopy: string) => {
  navigator.clipboard.writeText(textToCopy);
};

/**
 * Get Name Initials
 * @param name               Name whose initials you want
 * @param defaultInitials?   Default Initials (optional)
 */
export const getNameInitials = (name: string, defaultInitials = 'FD') => {
  const words = name.split(' ');
  let initials = '';

  for (const word of words) {
    initials += word.substring(0, 1);
  }

  return initials === '' ? defaultInitials : initials;
};

export const toCamel = (o: any) => {
  var newO: any, origKey, newKey, value;
  if (o instanceof Array) {
    return o.map(function (value) {
      if (typeof value === 'object') {
        value = toCamel(value);
      }
      return value;
    });
  } else {
    newO = {};
    for (origKey in o) {
      if (o.hasOwnProperty(origKey)) {
        newKey = (origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey).toString();
        value = o[origKey];
        if (value instanceof Array || (value !== null && value.constructor === Object)) {
          value = toCamel(value);
        }
        newO[newKey] = value;
      }
    }
  }
  return newO;
};

/**
 * Get value
 * @param value               value to check for null, undefined, or an empty string
 */
export const isNullUndefinedEmpty = (value: any): any => {
  if (value === null || value === undefined || value.trim() === '') {
    return 'N/A';
  }
  return value;
};

export const getCurrentDateTime = () => {
  return momentCustomInstance().toISOString();
};

export const getDayOfWeekFromNumber = (number: any) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (number >= 1 && number <= 7) {
    return days[number - 1];
  } else {
    return 'Invalid day number';
  }
};

export const generateUUID24 = (): string => {

  const uuid = uuidv4();
  const uuidWithoutHyphens = uuid.replace(/-/g, '');
  const uuid24 = uuidWithoutHyphens.substring(0, 24);
  return uuid24;
}

export const displayToastNotificationErrors = (error: any, toastError :(message: string) => void) => {

const errors = error.errors;
for (const key in errors) {
  if (errors.hasOwnProperty(key)) {
      const errorMessages = errors[key];
      errorMessages.forEach((message: string) => {
        toastError(message);
      });
    }
  }
}

function toCamelCase(str: string, mappings: Record<string, string> = {}): string {
  if (mappings.hasOwnProperty(str)) {
    return mappings[str];
  }

  return str
    .replace(/([A-Z]+)([A-Z][a-z\d]*)/g, '$1 $2') // Add space between sequences of uppercase letters followed by lowercase letters or digits
    .replace(/([a-z\d])([A-Z])/g, '$1 $2') // Add space before capital letters preceded by lowercase letters or digits
    .toLowerCase() // Convert the entire string to lowercase
    .replace(/\b(\w)\b/g, (_, letter) => letter.toLowerCase()) // Convert separated single-letter words to lowercase
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => index === 0 ? word.toLowerCase() : word.toUpperCase()) // Capitalize letters following a space
    .replace(/\s+/g, ''); // Remove spaces
}

export const convertObjectToCamelCase = (obj: any, keyMappings: Record<string, string> = {}): any => {
  if (Array.isArray(obj)) {
    return obj.map(v => convertObjectToCamelCase(v, keyMappings));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = toCamelCase(key, keyMappings);
      return {
        ...result,
        [camelKey]: convertObjectToCamelCase(obj[key], keyMappings)
      };
    }, {});
  }
  return obj;
};

export function removeTextFromEnd(input: string, textToRemove: string): string {
  const lowerCaseInput = input.toLowerCase();
  const lowerCaseTextToRemove = textToRemove.toLowerCase();

  if (lowerCaseInput.endsWith(lowerCaseTextToRemove)) {
    return input.slice(0, -textToRemove.length).trim();
  }

  return input.trim();
}

export function getControlName(control: AbstractControl): string | null {
  if (control.parent instanceof FormGroup) {
    const formGroup = control.parent as FormGroup;

    for (const name in formGroup.controls) {
      if (formGroup.controls[name] === control) {
        return name;
      }
    }
  }
  return null;
}

// convert UTC to dynamic local time
export function formatToLocalTime(time: string) {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;  
  return moment.utc(time, 'YYYY-MM-DD HH:mm:ss.S').tz(userTimeZone).format('YYYY-MM-DD HH:mm:ss');
}

export function scrollToBottom(): void {
  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
}

export const formatTimeLeft = (timeLeft: number, isMinimal: boolean = false): string => {
  if (timeLeft >= 60) {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;

    if (isMinimal) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    return `${minutes} minute${minutes > 1 ? 's' : ''}${
      seconds > 0 ? ` and ${seconds} second${seconds > 1 ? 's' : ''}` : ''
    }`;
  }

  return isMinimal
    ? `0:${timeLeft.toString().padStart(2, '0')}`
    : `${timeLeft} second${timeLeft > 1 ? 's' : ''}`;
};

export const getType = (value: any) => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (value instanceof Date) return "date";
  if (value instanceof RegExp) return "regexp";
  return typeof value;
}

export const parseGoogleAddress = (addressComponents: any) => {

  const getComponent = (type: any, useShortName = false) => {
    const component = addressComponents.find((c: any) =>
      c.types.includes(type)
    );

    if (!component) return "";

    return component.short_name;
  };

  return {
    apartment: getComponent("subpremise"),

    streetNumber: getComponent("street_number"),

    street: getComponent("route"),

    city:
      getComponent("locality") ||
      getComponent("postal_town") ||
      getComponent("administrative_area_level_3"),

    county: getComponent("administrative_area_level_2"),

    state: getComponent("administrative_area_level_1"),

    stateCode: getComponent("administrative_area_level_1", true),

    zip: getComponent("postal_code"),

    country: getComponent("country"),

    countryCode: getComponent("country", true),

    fullAddress: [
      getComponent("street_number"),
      getComponent("route")
    ]
      .filter(Boolean)
      .join(" ")
  };
}